import type { IPlaybackScheduleRoot } from '../models/player-registration-payload'
import type { TGeoLatLng } from '../models/playlist-assignment-geo-filter'
import type { TFrame } from '../models/playlist'
import {
  isPointInsideGeoFilter,
  maxGeoFilterRadiusMeters
} from './playlistAssignmentGeoFilter'

/** True when the frame has a non-empty geographic gate. */
export function frameHasGeoFilter(
  frame: Pick<TFrame, 'geoFilter'>
): boolean {
  return frame.geoFilter != null && (frame.geoFilter.circles?.length ?? 0) > 0
}

/**
 * Whether a frame's optional geofilter is satisfied at playback time.
 *
 * - No filter → true.
 * - `geoPoint` omitted (undefined) → skip geo (time-only callers).
 * - `geoPoint: null` → unknown location; honor {@link showWhenLocationUnavailable}.
 * - Resolved point → inside any circle.
 */
export function isFrameGeoSatisfied(
  frame: Pick<TFrame, 'geoFilter' | 'showWhenLocationUnavailable'>,
  geoPoint: TGeoLatLng | null | undefined,
  options?: { skipGeoFilter?: boolean }
): boolean {
  if (options?.skipGeoFilter === true) {
    return true
  }
  if (!frameHasGeoFilter(frame)) {
    return true
  }
  if (geoPoint === undefined) {
    return true
  }
  if (geoPoint == null) {
    return frame.showWhenLocationUnavailable === true
  }
  return isPointInsideGeoFilter(geoPoint, frame.geoFilter ?? null)
}

/** Scan assignment playlist frames for any geofilter. */
export function scheduleAssignmentsHaveFrameGeoFilter(
  assignments: ReadonlyArray<{ playlist?: { frames?: readonly TFrame[] } | null }>
): boolean {
  for (const a of assignments) {
    for (const frame of a.playlist?.frames ?? []) {
      if (frameHasGeoFilter(frame)) {
        return true
      }
    }
  }
  return false
}

/** True when any frame in `frames` arrays carries a geofilter. */
export function framesCollectionHasGeoFilter(
  framesCollections: ReadonlyArray<readonly TFrame[] | undefined>
): boolean {
  for (const frames of framesCollections) {
    for (const frame of frames ?? []) {
      if (frameHasGeoFilter(frame)) {
        return true
      }
    }
  }
  return false
}

/** Scan frames (parent rows + embedded map) for any geofilter. */
export function playbackScheduleHasFrameGeoFilter(
  root: Pick<IPlaybackScheduleRoot, 'playlists'> & {
    embeddedPlaylistFrames?: IPlaybackScheduleRoot['embeddedPlaylistFrames']
  } | null | undefined
): boolean {
  if (root == null) {
    return false
  }
  for (const pl of root.playlists ?? []) {
    for (const frame of pl.frames ?? []) {
      if (frameHasGeoFilter(frame)) {
        return true
      }
    }
  }
  for (const frames of Object.values(root.embeddedPlaylistFrames ?? {})) {
    for (const frame of frames) {
      if (frameHasGeoFilter(frame)) {
        return true
      }
    }
  }
  return false
}

/** Largest frame geofilter radius on the schedule (for GPS accuracy ceiling). */
export function maxFrameGeoFilterRadiusMeters(
  root: Pick<IPlaybackScheduleRoot, 'playlists'> & {
    embeddedPlaylistFrames?: IPlaybackScheduleRoot['embeddedPlaylistFrames']
  } | null | undefined
): number | null {
  if (root == null) {
    return null
  }
  let max: number | null = null
  const consider = (frame: TFrame) => {
    const r = maxGeoFilterRadiusMeters(frame.geoFilter ?? null)
    if (r == null) {
      return
    }
    if (max == null || r > max) {
      max = r
    }
  }
  for (const pl of root.playlists ?? []) {
    for (const frame of pl.frames ?? []) {
      consider(frame)
    }
  }
  for (const frames of Object.values(root.embeddedPlaylistFrames ?? {})) {
    for (const frame of frames) {
      consider(frame)
    }
  }
  return max
}
